import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import { KeyboardEvent, useEffect } from "react";
import { object, string } from "yup";
import { Button, Form, Input } from "../../common/components";
import { DialogTitleWithCloseButton } from "../../common/components/Dialog/DialogTitleWithCloseButton";
import { useForm } from "../../common/hooks/useForm";

const validationSchema = object({
  value: string().required("general.errors.required.input"),
});

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (value: string) => void;
  defaultValue: string;
  title: string;
  label?: string;
  ctaButtonText?: string;
}

const EditModal = ({
  open,
  onClose,
  onSave,
  title,
  ctaButtonText,
  defaultValue = "",
}: Props) => {
  const { t } = useTranslation();

  const formik = useForm({
    initialValues: { value: defaultValue },
    onSubmit: (values) => {
      onSave(values.value);
      onClose();
    },
    validationSchema,
  });

  useEffect(() => {
    if (open) {
      formik.setFieldValue("value", defaultValue);
    }
    // eslint-disable-next-line
  }, [defaultValue, open]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth={true}
      maxWidth={"md"}
      onKeyDown={handleKeyDown}
    >
      <Form formik={formik}>
        <DialogTitleWithCloseButton
          id="customized-dialog-title"
          onClose={onClose}
          sx={{ mb: 2 }}
        >
          {title}
        </DialogTitleWithCloseButton>
        <DialogContent sx={{ pr: 0 }}>
          <Input
            type="text"
            name="value"
            sx={{ maxWidth: "unset" }}
            value={formik.values["value"]}
            noMaxWidth
            autoFocus
            data-test="edit.input"
          />
        </DialogContent>
        <DialogActions>
          <Button type="submit">
            <Typography variant="actionBoldBigIcon">
              {ctaButtonText || t("general.save")}
            </Typography>
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

export default EditModal;
